import { LEVEL_CONFIG } from '../../../config/level.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  jumpArc,
  laneArea,
  moduleSides,
  obstacleRow,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type CollectibleSpawnPoint, type FixedObstaclePlacement, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_21 – earthworks 40 m (design/01_segments/SEG_21_Earthworks_Nature): site road of compacted earth
 * in open terrain, a trench across the track and heaps of excavated soil.
 *
 *   z 8       dirt mounds in two lanes (jump or change lane)
 *   z 16–19   trench over all lanes, 1.2 m deep (jump)
 *   z 27      random obstacle row
 *   z 34      dirt mounds again, other lanes
 *
 * The Blender module (tools/blender/segments/SEG_21_Earthworks.py) is built from these numbers.
 */
export const EARTHWORKS = {
  length: 40,
  trench: { zFrom: 16, zTo: 19, depth: 1.2 },
  moundRows: { first: 8, second: 34 },
  randomRowZ: 27,
} as const;

const LANES = [0, 1, 2] as const;
const SPACING = 2;
const ARC_OVERHANG = 1.5;
const { trench, moundRows } = EARTHWORKS;
const MOUND_TYPES = [ObstacleType.Jump] as const;
const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange, ObstacleType.Slide] as const;

/** Mounds in two lanes at the first row and in the two others at the second (`open` = free lane first). */
function mounds(open: number): FixedObstaclePlacement[] {
  const second = (open + 1) % LANES.length;
  return [
    ...LANES.filter((lane) => lane !== open).map((lane) => ({ spawnPointId: `SOCKET_OBS_M08_L${lane}`, obstacleId: 'OBS_DIRT_MOUND_A' })),
    ...LANES.filter((lane) => lane !== second).map((lane) => ({ spawnPointId: `SOCKET_OBS_M34_L${lane}`, obstacleId: 'OBS_DIRT_MOUND_A' })),
  ];
}

/** Bottles: every lane, arcing over the trench. */
function bottles(): CollectibleSpawnPoint[] {
  const center = (trench.zFrom + trench.zTo) / 2;
  const half = (trench.zTo - trench.zFrom) / 2 + ARC_OVERHANG;
  const arc = jumpArc(COLLECTIBLE_FLOAT_HEIGHT, LEVEL_CONFIG.collectibleJumpArcHeight, center, half);
  const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
  return LANES.flatMap((lane) =>
    collectibleLine(WORLD_CONFIG, `TRENCH_L${lane}`, [
      { lane, from: 1, to: center - half - 1, spacing: SPACING, height: flat },
      { lane, from: center - half, to: center + half, spacing: 1.5, height: arc },
      { lane, from: center + half + 1.5, to: EARTHWORKS.length - 1, spacing: SPACING, height: flat },
    ]),
  );
}

export const SEG_EARTHWORKS_40M: SegmentDefinition = {
  id: 'SEG_EARTHWORKS_40M',
  kind: SegmentKind.Straight,
  displayName: 'Erdarbeiten 40 m',
  length: EARTHWORKS.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_EARTHWORKS_40M',

  entry: entryConnector(),
  exits: [straightExit(EARTHWORKS.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [{ id: 'GAP_TRENCH', ...laneArea(WORLD_CONFIG, 0, LANES.length - 1, trench.zFrom, trench.zTo), depth: trench.depth }],
    ceilings: [],
  },

  obstacleSpawnPoints: [
    ...obstacleRowOn(WORLD_CONFIG, 'M08', moundRows.first, LANES, MOUND_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R27', EARTHWORKS.randomRowZ, ROW_TYPES),
    ...obstacleRowOn(WORLD_CONFIG, 'M34', moundRows.second, LANES, MOUND_TYPES),
  ],
  obstacleRowLayouts: { sparse: ['R27'], dense: ['R27'] },
  obstacleVariants: LANES.map((open) => ({ id: `OPEN_L${open}`, weight: 1, placements: mounds(open) })),

  collectibleSpawnPoints: bottles(),

  // Open terrain (nature look): single ground props behind the fence, no roadside bays.
  ...moduleSides(EARTHWORKS.length, { roadside: false }),

  generation: {
    weight: 0.2,
    minDistance: 250,
    minSegmentsAfterJunction: 1,
    family: 'EARTHWORKS',
    tags: ['nature', 'terrain', 'pit'],
  },
};
